<template>
  <Dialog 
    v-model:visible="dialogVisible" 
    :header="`Gestión de Pagos — ${tenant?.name}`" 
    modal 
    :style="{ width: '800px' }"
    :maximizable="true"
    @hide="onHide"
  >
    <TabView>
      <!-- Tab 1: Asignar Plan -->
      <TabPanel header="📅 Asignar Plan">
        <div class="tab-content">
          <p class="tab-description">Selecciona un plan de suscripción para el tenant y genera un enlace de pago.</p>
          
          <div class="field">
            <label for="plan-select">Plan de Suscripción</label>
            <Dropdown
              id="plan-select"
              v-model="selectedPlan"
              :options="plans"
              optionLabel="name"
              placeholder="Selecciona un plan"
              class="w-full"
              :loading="loadingPlans"
            >
              <template #value="slotProps">
                <div v-if="slotProps.value" class="plan-option">
                  <span>{{ slotProps.value.name }}</span>
                  <span class="plan-price">{{ formatAmount(slotProps.value.monthly_price) }}</span>
                </div>
                <span v-else>{{ slotProps.placeholder }}</span>
              </template>
              <template #option="slotProps">
                <div class="plan-option">
                  <div>
                    <div class="plan-name">{{ slotProps.option.name }}</div>
                    <div class="plan-details">
                      {{ slotProps.option.included_tokens?.toLocaleString('es-CO') }} tokens/mes
                    </div>
                  </div>
                  <span class="plan-price">{{ formatAmount(slotProps.option.monthly_price) }}</span>
                </div>
              </template>
            </Dropdown>
          </div>

          <Button
            label="Generar enlace"
            icon="pi pi-link"
            @click="generateSubscriptionLink"
            :disabled="!selectedPlan"
            :loading="generatingLink"
            class="generate-btn"
          />

          <div v-if="generatedLink" class="generated-link-section">
            <label>Enlace de Pago Generado</label>
            <div class="link-display">
              <InputText
                :value="generatedLink"
                readonly
                class="link-input"
              />
              <Button
                icon="pi pi-copy"
                @click="copyToClipboard(generatedLink)"
                severity="secondary"
                v-tooltip.top="'Copiar enlace'"
              />
            </div>
            <small class="link-hint">Comparte este enlace con el tenant para que complete el pago.</small>
          </div>
        </div>
      </TabPanel>

      <!-- Tab 2: Asignar Tokens -->
      <TabPanel header="🎁 Asignar Tokens">
        <div class="tab-content">
          <p class="tab-description">Selecciona un paquete de tokens predefinido o crea uno personalizado.</p>
          
          <div class="token-packages-section">
            <h4>Paquetes Predefinidos</h4>
            <div v-if="loadingPackages" class="loading-state">
              <ProgressSpinner style="width: 50px; height: 50px" />
            </div>
            <div v-else class="token-packages-grid">
              <div
                v-for="pkg in tokenPackages"
                :key="pkg.id"
                class="token-package-card"
                :class="{ selected: selectedPackage?.id === pkg.id }"
                @click="selectPackage(pkg)"
              >
                <div class="package-header">
                  <i class="pi pi-box"></i>
                  <h5>{{ pkg.name }}</h5>
                </div>
                <div class="package-tokens">
                  {{ pkg.token_count?.toLocaleString('es-CO') }} tokens
                </div>
                <div class="package-price">
                  {{ formatAmount(pkg.price_cop) }}
                </div>
              </div>
            </div>

            <Button
              v-if="selectedPackage"
              label="Generar enlace para paquete seleccionado"
              icon="pi pi-link"
              @click="generateTokenPackageLink"
              :loading="generatingLink"
              class="generate-btn"
            />
          </div>

          <Divider />

          <div class="custom-tokens-section">
            <h4>Paquete Personalizado</h4>
            <div class="custom-fields">
              <div class="field">
                <label for="custom-token-count">Cantidad de Tokens</label>
                <InputNumber
                  id="custom-token-count"
                  v-model="customTokenCount"
                  :min="1"
                  :step="1000"
                  placeholder="Ej: 50000"
                  class="w-full"
                  :useGrouping="true"
                  locale="es-CO"
                />
              </div>
              <div class="field">
                <label for="custom-token-price">Precio (COP centavos)</label>
                <InputNumber
                  id="custom-token-price"
                  v-model="customTokenPrice"
                  :min="1"
                  :step="100"
                  placeholder="Ej: 5000000 (50,000 COP)"
                  class="w-full"
                  :useGrouping="true"
                  locale="es-CO"
                />
                <small class="field-hint">Ingresa el precio en centavos (100 centavos = 1 COP)</small>
              </div>
            </div>

            <Button
              label="Generar enlace personalizado"
              icon="pi pi-link"
              @click="generateCustomTokenLink"
              :disabled="!customTokenCount || !customTokenPrice"
              :loading="generatingLink"
              class="generate-btn"
            />
          </div>

          <div v-if="generatedLink" class="generated-link-section">
            <label>Enlace de Pago Generado</label>
            <div class="link-display">
              <InputText
                :value="generatedLink"
                readonly
                class="link-input"
              />
              <Button
                icon="pi pi-copy"
                @click="copyToClipboard(generatedLink)"
                severity="secondary"
                v-tooltip.top="'Copiar enlace'"
              />
            </div>
            <small class="link-hint">Comparte este enlace con el tenant para que complete el pago.</small>
          </div>
        </div>
      </TabPanel>

      <!-- Tab 3: Pago Personalizado -->
      <TabPanel header="💰 Pago Personalizado">
        <div class="tab-content">
          <p class="tab-description">Crea un pago personalizado con un monto y descripción específicos.</p>
          
          <div class="field">
            <label for="custom-amount">Monto (COP centavos)</label>
            <InputNumber
              id="custom-amount"
              v-model="customAmount"
              :min="1"
              :step="100"
              placeholder="Ej: 10000000 (100,000 COP)"
              class="w-full"
              :useGrouping="true"
              locale="es-CO"
            />
            <small class="field-hint">Ingresa el monto en centavos (100 centavos = 1 COP)</small>
          </div>

          <div class="field">
            <label for="custom-description">Descripción</label>
            <Textarea
              id="custom-description"
              v-model="customDescription"
              rows="3"
              placeholder="Describe el concepto del pago..."
              class="w-full"
            />
          </div>

          <Button
            label="Generar enlace"
            icon="pi pi-link"
            @click="generateCustomPaymentLink"
            :disabled="!customAmount || !customDescription"
            :loading="generatingLink"
            class="generate-btn"
          />

          <div v-if="generatedLink" class="generated-link-section">
            <label>Enlace de Pago Generado</label>
            <div class="link-display">
              <InputText
                :value="generatedLink"
                readonly
                class="link-input"
              />
              <Button
                icon="pi pi-copy"
                @click="copyToClipboard(generatedLink)"
                severity="secondary"
                v-tooltip.top="'Copiar enlace'"
              />
            </div>
            <small class="link-hint">Comparte este enlace con el tenant para que complete el pago.</small>
          </div>
        </div>
      </TabPanel>

      <!-- Tab 4: Historial -->
      <TabPanel header="📜 Historial">
        <div class="tab-content">
          <div v-if="loadingOrders" class="loading-state">
            <ProgressSpinner style="width: 50px; height: 50px" />
          </div>
          <TenantPaymentHistory
            v-else
            :orders="orders"
            :isAdmin="true"
            @link-regenerated="onLinkRegenerated"
          />
        </div>
      </TabPanel>
    </TabView>
  </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import Dialog from 'primevue/dialog';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Divider from 'primevue/divider';
import ProgressSpinner from 'primevue/progressspinner';
import TenantPaymentHistory from './TenantPaymentHistory.vue';
import api from '../../services/api';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  tenant: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:visible', 'payment-created']);

const toast = useToast();

// Dialog visibility
const dialogVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

// Data
const plans = ref([]);
const tokenPackages = ref([]);
const orders = ref([]);

// Loading states
const loadingPlans = ref(false);
const loadingPackages = ref(false);
const loadingOrders = ref(false);
const generatingLink = ref(false);

// Selected items
const selectedPlan = ref(null);
const selectedPackage = ref(null);

// Custom token fields
const customTokenCount = ref(null);
const customTokenPrice = ref(null);

// Custom payment fields
const customAmount = ref(null);
const customDescription = ref('');

// Generated link
const generatedLink = ref('');

// Watch for dialog visibility changes to load data
watch(() => props.visible, (newVal) => {
  if (newVal && props.tenant) {
    loadData();
  } else {
    resetForm();
  }
});

// Load all data when dialog opens
const loadData = async () => {
  await Promise.all([
    loadPlans(),
    loadTokenPackages(),
    loadOrders()
  ]);
};

// Load active plans
const loadPlans = async () => {
  loadingPlans.value = true;
  try {
    const response = await api.get('/payments/plans');
    plans.value = response.data.filter(plan => plan.is_active);
  } catch (error) {
    console.error('Error loading plans:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudieron cargar los planes',
      life: 3000
    });
  } finally {
    loadingPlans.value = false;
  }
};

// Load active token packages
const loadTokenPackages = async () => {
  loadingPackages.value = true;
  try {
    const response = await api.get('/payments/token-packages');
    tokenPackages.value = response.data.filter(pkg => pkg.is_active);
  } catch (error) {
    console.error('Error loading token packages:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudieron cargar los paquetes de tokens',
      life: 3000
    });
  } finally {
    loadingPackages.value = false;
  }
};

// Load payment orders for tenant
const loadOrders = async () => {
  if (!props.tenant?.id) return;
  
  loadingOrders.value = true;
  try {
    const response = await api.get('/payments/orders', {
      params: { tenant_id: props.tenant.id }
    });
    orders.value = response.data;
  } catch (error) {
    console.error('Error loading orders:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudieron cargar los pagos',
      life: 3000
    });
  } finally {
    loadingOrders.value = false;
  }
};

// Generate subscription payment link
const generateSubscriptionLink = async () => {
  if (!selectedPlan.value || !props.tenant) return;

  generatingLink.value = true;
  generatedLink.value = '';

  try {
    const response = await api.post('/payments/generate-link', {
      tenant_id: props.tenant.id,
      payment_type: 'subscription',
      plan_id: selectedPlan.value.id
    });

    generatedLink.value = response.data.payment_link_url;

    toast.add({
      severity: 'success',
      summary: 'Enlace Generado',
      detail: 'El enlace de pago ha sido generado exitosamente',
      life: 5000
    });

    // Reload orders to show the new one
    await loadOrders();
    emit('payment-created');
  } catch (error) {
    console.error('Error generating subscription link:', error);
    const errorMessage = error.response?.data?.detail || 'Error al generar el enlace de pago';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    generatingLink.value = false;
  }
};

// Generate token package payment link
const generateTokenPackageLink = async () => {
  if (!selectedPackage.value || !props.tenant) return;

  generatingLink.value = true;
  generatedLink.value = '';

  try {
    const response = await api.post('/payments/generate-link', {
      tenant_id: props.tenant.id,
      payment_type: 'token_package',
      token_package_id: selectedPackage.value.id
    });

    generatedLink.value = response.data.payment_link_url;

    toast.add({
      severity: 'success',
      summary: 'Enlace Generado',
      detail: 'El enlace de pago ha sido generado exitosamente',
      life: 5000
    });

    // Reload orders
    await loadOrders();
    emit('payment-created');
  } catch (error) {
    console.error('Error generating token package link:', error);
    const errorMessage = error.response?.data?.detail || 'Error al generar el enlace de pago';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    generatingLink.value = false;
  }
};

// Generate custom token package link
const generateCustomTokenLink = async () => {
  if (!customTokenCount.value || !customTokenPrice.value || !props.tenant) return;

  generatingLink.value = true;
  generatedLink.value = '';

  try {
    const response = await api.post('/payments/generate-link', {
      tenant_id: props.tenant.id,
      payment_type: 'token_package',
      custom_token_count: customTokenCount.value,
      custom_amount: customTokenPrice.value
    });

    generatedLink.value = response.data.payment_link_url;

    toast.add({
      severity: 'success',
      summary: 'Enlace Generado',
      detail: 'El enlace de pago personalizado ha sido generado exitosamente',
      life: 5000
    });

    // Reload orders
    await loadOrders();
    emit('payment-created');
  } catch (error) {
    console.error('Error generating custom token link:', error);
    const errorMessage = error.response?.data?.detail || 'Error al generar el enlace de pago';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    generatingLink.value = false;
  }
};

// Generate custom payment link
const generateCustomPaymentLink = async () => {
  if (!customAmount.value || !customDescription.value || !props.tenant) return;

  generatingLink.value = true;
  generatedLink.value = '';

  try {
    const response = await api.post('/payments/generate-link', {
      tenant_id: props.tenant.id,
      payment_type: 'custom',
      custom_amount: customAmount.value,
      custom_description: customDescription.value
    });

    generatedLink.value = response.data.payment_link_url;

    toast.add({
      severity: 'success',
      summary: 'Enlace Generado',
      detail: 'El enlace de pago ha sido generado exitosamente',
      life: 5000
    });

    // Reload orders
    await loadOrders();
    emit('payment-created');
  } catch (error) {
    console.error('Error generating custom payment link:', error);
    const errorMessage = error.response?.data?.detail || 'Error al generar el enlace de pago';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: errorMessage,
      life: 5000
    });
  } finally {
    generatingLink.value = false;
  }
};

// Select a token package
const selectPackage = (pkg) => {
  selectedPackage.value = pkg;
  // Clear custom fields when selecting a predefined package
  customTokenCount.value = null;
  customTokenPrice.value = null;
  generatedLink.value = '';
};

// Copy link to clipboard
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.add({
      severity: 'success',
      summary: 'Copiado',
      detail: 'El enlace ha sido copiado al portapapeles',
      life: 3000
    });
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo copiar el enlace',
      life: 3000
    });
  }
};

// Format amount in COP
const formatAmount = (amountCentavos) => {
  const amount = amountCentavos / 100;
  return `${amount.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} COP`;
};

// Handle link regenerated event
const onLinkRegenerated = async (updatedOrder) => {
  // Reload orders to show updated data
  await loadOrders();
  
  // Show the regenerated link
  generatedLink.value = updatedOrder.payment_link_url;
};

// Reset form when dialog closes
const resetForm = () => {
  selectedPlan.value = null;
  selectedPackage.value = null;
  customTokenCount.value = null;
  customTokenPrice.value = null;
  customAmount.value = null;
  customDescription.value = '';
  generatedLink.value = '';
};

// Handle dialog hide
const onHide = () => {
  resetForm();
};
</script>

<style scoped>
.tab-content {
  padding: 1rem 0;
}

.tab-description {
  color: #6b7280;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.field {
  margin-bottom: 1.5rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.field-hint {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.w-full {
  width: 100%;
}

.plan-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.plan-name {
  font-weight: 500;
  color: #374151;
}

.plan-details {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.plan-price {
  font-weight: 600;
  color: #6366f1;
}

.generate-btn {
  margin-top: 1rem;
}

.generated-link-section {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.generated-link-section label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.link-display {
  display: flex;
  gap: 0.5rem;
}

.link-input {
  flex: 1;
}

.link-hint {
  display: block;
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.85rem;
}

/* Token Packages Section */
.token-packages-section {
  margin-bottom: 2rem;
}

.token-packages-section h4 {
  margin-bottom: 1rem;
  color: #374151;
  font-size: 1rem;
}

.token-packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.token-package-card {
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.token-package-card:hover {
  border-color: #6366f1;
  background-color: #f9fafb;
}

.token-package-card.selected {
  border-color: #6366f1;
  background-color: #eef2ff;
}

.package-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.package-header i {
  font-size: 1.5rem;
  color: #6366f1;
}

.package-header h5 {
  margin: 0;
  font-size: 0.95rem;
  color: #374151;
}

.package-tokens {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.package-price {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6366f1;
}

/* Custom Tokens Section */
.custom-tokens-section h4 {
  margin-bottom: 1rem;
  color: #374151;
  font-size: 1rem;
}

.custom-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

/* Loading State */
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .custom-fields {
    grid-template-columns: 1fr;
  }

  .token-packages-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
}
</style>
